// The two client mode switches of PLAN.md D8, as pure functions over stored
// notes and the queued sync ops. Reference for the Swift and Kotlin ports,
// which run the same syncModeSwitch.fixtures.json. Change all three or none.
//
// legacy -> v2 (once per device, after the pull whose collabFeatures.syncV2
//   turns on is applied, and only once canUpgradeToV2 says no deliverable
//   legacy note work is left, or upgradeDecision forces it after
//   MAX_UPGRADE_DEFERRALS deferred pulls):
//   every note in the engine gets initialSyncState({rev, base: base_content,
//   content}); a synced note whose text differs from its base (its push landed,
//   the pull has not brought it back) takes that text as its base at rev 0, so
//   the first catch-up (a snapshot) adopts the server's text instead of sending
//   the pushed edit again; a note still pending or in conflict enters with no
//   base at rev 0: the first catch-up adopts the server's text and keeps
//   differing local text as "Copy of …" (no transform, nothing doubles);
//   queued note updates lose their text fields (an op left empty is
//   dropped). A note whose `create` is still
//   queued stays outside the engine, and its ops are untouched: the create
//   carries the text (the queue folds the updates into it).
// v2 -> legacy (the flag went off, or /sync/content answered `disabled`):
//   every note with a change in flight or unsent edits leaves the engine with
//   one queued legacy update {content, title, updated_at, expected_updated_at:
//   base_updated_at}, except a note shared with me (legacy cannot send a
//   member's text) or one without a common base (nothing to check against):
//   its unsent text is kept as "Copy of …" (`copies`) and the note goes back
//   to its base text (no base: its text stays, marked synced). Today's 409 and
//   diff3 path handles the rest.

import type { SyncOperation } from "@caliu/types"
import { initialSyncState } from "./noteContentSync"
import { isIdentityOp, type TextOp } from "./textOps"

/** A note row as the clients store it ("Client engine" in PLAN.md). */
export interface StoredNoteSync {
  id: string
  /** The server rev `base_content` is at (0 = unknown). */
  rev: number
  /** Server text at `rev` (null = no common ancestor). */
  base_content: string | null
  base_updated_at: string
  content: string
  title: string
  inflight_client_id: string | null
  inflight_seq: number | null
  inflight_base_rev: number | null
  inflight_ops: TextOp | null
  /** The buffer. Null = outside the engine (legacy mode, or a create still queued). */
  pending_ops: TextOp | null
  sync_status: "synced" | "pending" | "conflict"
  /** The note's owner (absent or null = this account's own note). */
  owner_id?: string | null
}

/** What the switches need to know about this device's legacy queue and account. */
export interface ModeSwitchContext {
  /** A queued op with `retries >= maxRetries` is never sent again (the push skips it). */
  maxRetries: number
  /** The signed-in account (null = unknown: no note counts as shared with me). */
  meId: string | null
}

/** A note someone else owns (`isSharedWithMe` in the clients). */
export function isNoteSharedWithMe(n: Pick<StoredNoteSync, "owner_id">, meId: string | null): boolean {
  return !!n.owner_id && !!meId && n.owner_id !== meId
}

/** Pulls the upgrade may wait for legacy work before it runs anyway. */
export const MAX_UPGRADE_DEFERRALS = 3

/**
 * What to do with a pull that wants v2 while the device is legacy. `deferrals`
 * is the number of pulls deferred so far (stored per device, reset when the
 * device switches). "force" upgrades and reports `sync_upgrade_forced`.
 */
export function upgradeDecision(canUpgrade: boolean, deferrals: number): "upgrade" | "defer" | "force" {
  if (canUpgrade) return "upgrade"
  return deferrals >= MAX_UPGRADE_DEFERRALS ? "force" : "defer"
}

/**
 * Fields of a queued note `update` that carry its text. A level 2 push sends
 * text through `/sync/content`, and the worker ignores these on a level 2
 * update (`CONTENT_FIELDS` in `apps/worker/src/lib/syncOperations.ts`).
 */
export const NOTE_TEXT_OP_FIELDS = [
  "content",
  "title",
  "word_count",
  "wordCount",
  "updated_at",
  "updatedAt",
  "expected_updated_at",
  "expectedUpdatedAt",
  // A history restore's flag means nothing without its text.
  "preserve_history",
  "preserveHistory",
] as const

/** Note ids whose `create` is still queued: outside the engine. */
function queuedCreates(queue: SyncOperation[]): Set<string> {
  const ids = new Set<string>()
  for (const op of queue) if (op.entity === "note" && op.type === "create") ids.add(op.entityId)
  return ids
}

/**
 * A queued note update without its text fields, or null when nothing else is
 * left in it (the op is dropped). Other ops come back as they are.
 */
export function stripNoteTextFields(op: SyncOperation): SyncOperation | null {
  if (op.entity !== "note" || op.type !== "update") return op
  const data = { ...((op.data ?? {}) as Record<string, unknown>) }
  let changed = false
  for (const k of NOTE_TEXT_OP_FIELDS) {
    if (k in data) {
      delete data[k]
      changed = true
    }
  }
  if (!changed) return op
  if (Object.keys(data).length === 0) return null
  return { ...op, data }
}

/**
 * Whether legacy -> v2 may run now. Legacy moves a note's base only when a
 * pull brings its change back and skips pending notes, so a pending note can
 * hold a base older than text it already pushed. Wait (stay legacy this
 * cycle, try again on the next pull) while legacy still has note work it can
 * deliver: a queued note op with retries left, or a pending note with no
 * queued op (the zombie sweep marks it synced). Never waited for: ops of
 * other entities, a conflict and its held ops (the upgrade clears it), ops
 * past `maxRetries` (never sent again) and anything of a note shared with me
 * (legacy refuses a member's text). Whatever is left pending enters the
 * engine with no base (`upgradeToV2`).
 */
export function canUpgradeToV2(notes: StoredNoteSync[], queue: SyncOperation[], ctx: ModeSwitchContext): boolean {
  const ignored = new Set(
    notes.filter((n) => n.sync_status === "conflict" || isNoteSharedWithMe(n, ctx.meId)).map((n) => n.id),
  )
  const noteOps = queue.filter((op) => op.entity === "note" && !ignored.has(op.entityId))
  if (noteOps.some((op) => op.retries < ctx.maxRetries)) return false
  const queued = new Set(noteOps.map((op) => op.entityId))
  return !notes.some((n) => n.sync_status === "pending" && !ignored.has(n.id) && !queued.has(n.id))
}

export function upgradeToV2(
  notes: StoredNoteSync[],
  queue: SyncOperation[],
): { notes: StoredNoteSync[]; queue: SyncOperation[] } {
  const creates = queuedCreates(queue)
  const outNotes = notes.map((n): StoredNoteSync => {
    const status = n.sync_status === "conflict" ? "pending" : n.sync_status
    if (creates.has(n.id)) {
      return { ...n, inflight_client_id: null, inflight_seq: null, inflight_base_rev: null, inflight_ops: null, pending_ops: null, sync_status: status }
    }
    // Legacy only moves the base when a pull brings a change back, so a landed
    // push leaves the base behind: diffing it would send that edit twice.
    const pushed = n.sync_status === "synced" && n.base_content !== null && n.base_content !== n.content
    // Pending or conflict: whether its text landed is unknown, so no base. The
    // first snapshot adopts the server's text, local text differing becomes a copy.
    const unsure = n.sync_status !== "synced"
    const rev = pushed || unsure ? 0 : n.rev
    const base = unsure ? null : pushed ? n.content : n.base_content
    const s = initialSyncState({ rev, base, baseUpdatedAt: n.base_updated_at, content: n.content })
    return {
      ...n,
      rev,
      base_content: base,
      inflight_client_id: null,
      inflight_seq: null,
      inflight_base_rev: null,
      inflight_ops: null,
      pending_ops: s.buffer,
      sync_status: status,
    }
  })
  const outQueue: SyncOperation[] = []
  for (const op of queue) {
    if (op.entity === "note" && creates.has(op.entityId)) {
      outQueue.push(op)
      continue
    }
    const kept = stripNoteTextFields(op)
    if (kept) outQueue.push(kept)
  }
  return { notes: outNotes, queue: outQueue }
}

/** Whether a note in the engine holds text the server has not confirmed. */
export function hasUnsentText(n: StoredNoteSync): boolean {
  if (n.inflight_seq !== null) return true
  // No common base: the buffer is an identity, but text typed here is still unsent.
  if (n.pending_ops !== null && n.base_content === null && n.content !== "" && n.sync_status !== "synced") return true
  return n.pending_ops !== null && !isIdentityOp(n.pending_ops)
}

/** Unsent text of a note the downgrade cannot queue: the caller saves it as "Copy of …". */
export interface DowngradeCopy {
  noteId: string
  text: string
}

export function downgradeToLegacy(
  notes: StoredNoteSync[],
  { now, timestamp, meId }: { now: string; timestamp: number; meId: string | null },
): { notes: StoredNoteSync[]; enqueue: SyncOperation[]; copies: DowngradeCopy[] } {
  const enqueue: SyncOperation[] = []
  const copies: DowngradeCopy[] = []
  const outNotes = notes.map((n): StoredNoteSync => {
    const unsent = hasUnsentText(n)
    const cleared = {
      ...n,
      inflight_client_id: null,
      inflight_seq: null,
      inflight_base_rev: null,
      inflight_ops: null,
      pending_ops: null,
    }
    if (!unsent) return cleared
    // Legacy refuses a member's text, and without a base the update would
    // overwrite blind: keep the text as a copy instead.
    if (isNoteSharedWithMe(n, meId) || n.base_content === null) {
      copies.push({ noteId: n.id, text: n.content })
      // Without a base the text stays (it is safe in the copy) and becomes the
      // base at rev 0: legacy diff3 gets an ancestor, and a re-upgrade rebases
      // an identity buffer instead of keeping the text as a second copy. The
      // caller re-fetches, so legacy rewrites the row with the server's text.
      if (n.base_content === null) return { ...cleared, rev: 0, base_content: n.content, sync_status: "synced" }
      return { ...cleared, content: n.base_content, sync_status: "synced" }
    }
    const data: Record<string, unknown> = { content: n.content, title: n.title, updated_at: now }
    // No base timestamp (never caught up): nothing to check against, as today's op without one.
    if (n.base_updated_at !== "") data.expected_updated_at = n.base_updated_at
    enqueue.push({ type: "update", entity: "note", entityId: n.id, data, timestamp, retries: 0 })
    return { ...cleared, sync_status: "pending" }
  })
  return { notes: outNotes, enqueue, copies }
}
